import { useState, useEffect } from 'react';

import { ImageIcon } from 'lucide-react';

import { messagingApi } from '@/lib/api/messaging.api';
import { cn } from '@/lib/utils';

import { MessageStatusIcon } from './MessageStatusIcon';

import type { MessagingMessage } from '@/lib/api/messaging.api';

const formatTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const MediaImage = ({ mediaUrl }: { mediaUrl: string }) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    messagingApi
      .getMediaSignedUrl(mediaUrl)
      .then((url) => {
        if (!cancelled) setSignedUrl(url);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mediaUrl]);

  if (loading) {
    return (
      <div className="flex h-32 w-48 items-center justify-center rounded-lg bg-black/10">
        <div className="border-primary h-5 w-5 animate-spin rounded-full border-b-2" />
      </div>
    );
  }

  if (error || !signedUrl) {
    return (
      <div className="flex h-32 w-48 items-center justify-center rounded-lg bg-black/10">
        <ImageIcon className="h-6 w-6 opacity-40" />
      </div>
    );
  }

  return (
    <img
      src={signedUrl}
      alt="Shared image"
      className="max-h-64 max-w-full rounded-lg object-contain"
      loading="lazy"
    />
  );
};

const QuotedMessage = ({
  content,
  direction,
  isParentOutbound,
}: {
  content: string;
  direction: 'INBOUND' | 'OUTBOUND' | null;
  isParentOutbound: boolean;
}) => (
  <div
    className={cn(
      'mb-1 rounded-lg border-l-4 px-2.5 py-1.5',
      isParentOutbound
        ? 'border-primary-foreground/40 bg-primary/80'
        : 'border-primary/40 bg-muted/80',
    )}
  >
    <p
      className={cn(
        'text-[10px] font-semibold',
        isParentOutbound ? 'text-primary-foreground/70' : 'text-primary',
      )}
    >
      {direction === 'OUTBOUND' ? 'You' : 'Customer'}
    </p>
    <p
      className={cn(
        'line-clamp-2 text-xs break-words',
        isParentOutbound ? 'text-primary-foreground/80' : 'text-foreground/70',
      )}
    >
      {content}
    </p>
  </div>
);

export const MessageBubble = ({
  message,
  compact = false,
}: {
  message: MessagingMessage;
  /** Tighter spacing/typography for dense views like the multi-chat grid. */
  compact?: boolean;
}) => {
  const isOutbound = message.direction === 'OUTBOUND';
  const isImage = message.type === 'IMAGE' && !!message.media_url;
  const hasQuote = !!message.reply_to_content;

  return (
    <div className={cn('flex', isOutbound ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'rounded-2xl shadow-sm',
          compact ? 'max-w-[90%] px-2.5 py-1' : 'max-w-[min(80%,28rem)] px-3.5 py-2',
          isOutbound
            ? 'bg-primary text-primary-foreground rounded-br-md'
            : 'bg-muted rounded-bl-md',
        )}
      >
        {hasQuote && (
          <QuotedMessage
            content={message.reply_to_content!}
            direction={message.reply_to_direction ?? null}
            isParentOutbound={isOutbound}
          />
        )}
        {isImage && <MediaImage mediaUrl={message.media_url!} />}
        {isImage && message.content && message.content !== '[Image]' && (
          <p
            className={cn('mt-1 break-words whitespace-pre-wrap', compact ? 'text-xs' : 'text-sm')}
          >
            {message.content}
          </p>
        )}
        {!isImage && (
          <p className={cn('break-words whitespace-pre-wrap', compact ? 'text-xs' : 'text-sm')}>
            {message.content ?? ''}
          </p>
        )}
        <div
          className={cn(
            'flex items-center gap-1',
            compact ? 'mt-0.5' : 'mt-1',
            isOutbound ? 'justify-end' : 'justify-start',
          )}
        >
          <span className={cn('opacity-60', compact ? 'text-[9px]' : 'text-[10px]')}>
            {formatTime(message.created_at)}
          </span>
          {isOutbound && <MessageStatusIcon status={message.status} />}
        </div>
        {message.status === 'FAILED' && message.error_message && (
          <p className="mt-1 text-xs text-red-300">{message.error_message}</p>
        )}
      </div>
    </div>
  );
};
